/* eslint-disable react/no-unescaped-entities --
 * v77.99.77: App shell uses sentence-case copy with apostrophes ("You're
 * viewing client data"). Safe English text.
 */
import { useState, useEffect } from "react";
import { Outlet, NavLink, useNavigate, useLocation } from "react-router-dom";
import useDeepLinkSpotlight from "@/hooks/useDeepLinkSpotlight";
import { useAuth } from "@/context/AuthContext";
import { LayoutDashboard, MessageSquare, Calendar, ReceiptEuro, BookOpen, FolderLock, Users, Settings, LogOut, MoreHorizontal, Inbox, X, ShieldCheck, HelpCircle, FileDown, ListChecks, Bug, UserCog, Sparkles, Wallet, TrendingUp, ArrowLeftRight, Eye, CalendarClock, HandHelping, Star, Gavel } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle, SheetClose } from "@/components/ui/sheet";
import { Logo } from "@/components/Logo";
import { resolveMobileNavSlots, MOBILE_NAV_MODULES, moduleVisibleTo } from "@/lib/navModules";
import { ChildSwitcher } from "@/components/ChildSwitcher";
import { SubscriptionBanner } from "@/components/SubscriptionBanner";
import InstallPrompt from "@/components/InstallPrompt";
import TrialPill from "@/components/TrialPill";
import { NoChildrenAccessBanner } from "@/components/NoChildrenAccessBanner";
import { ThemeToggle } from "@/components/ThemeToggle";
import { BugReportDialog } from "@/components/BugReportDialog";
import { WhatsNewModal } from "@/components/WhatsNewModal";
import { LATEST_RELEASE_DATE as FALLBACK_LATEST_DATE, LATEST_RELEASE_VERSION as FALLBACK_LATEST_VERSION } from "@/lib/releaseNotes";
import { api } from "@/lib/api";

// Standard parent-facing navigation. The primary admin (operator) sees a
// completely different, admin-only nav further down — they never need
// Dashboard / Messages / Calendar / etc. on their own account.
const NAV = [
  { to: "/app/dashboard", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/app/messages", label: "Messages", icon: MessageSquare, testid: "nav-messages", permKey: "view_messages" },
  // v77.99.98 (iter 121) — "Parenting" section groups all the
  // schedule/contact rails together. Contact Visits is a top-level
  // evidence-grade module and must NOT sit under Court Bundle.
  { section: "Parenting" },
  { to: "/app/court-orders", label: "Court Orders", icon: Gavel, testid: "nav-court-orders" },
  { to: "/app/calendar", label: "Calendar", icon: Calendar, testid: "nav-calendar", permKey: "view_calendar" },
  { to: "/app/contact-visits", label: "Contact Visits", icon: HandHelping, testid: "nav-contact-visits" },
  { section: "Co-parenting" },
  { to: "/app/requests", label: "Requests", icon: Inbox, testid: "nav-requests", permKey: "view_requests" },
  { to: "/app/expenses", label: "Expenses", icon: ReceiptEuro, testid: "nav-expenses", permKey: "view_expenses" },
  // v77.99.86 — "Upcoming Expenses" promoted out of the Expenses page
  // into its own sibling so the planning module (estimates, reminders,
  // attachments, future Court Bundle integration) has room to grow
  // without bloating the Expenses page.
  { to: "/app/upcoming-expenses", label: "Upcoming Expenses", icon: CalendarClock, testid: "nav-upcoming-expenses", permKey: "view_upcoming_expenses" },
  // v77.99.32 — Maintenance sits next to Expenses because parents
  // think of them together (both = "money side of co-parenting").
  // Permission key reused so mediator/solicitor view-grants behave
  // consistently with the rest of the money rail.
  { to: "/app/maintenance", label: "Maintenance", icon: Wallet, testid: "nav-maintenance", permKey: "view_expenses" },
  { to: "/app/journal", label: "Journal", icon: BookOpen, testid: "nav-journal", permKey: "view_journal" },
  { to: "/app/documents", label: "Documents", icon: FolderLock, testid: "nav-documents", permKey: "view_documents" },
  { to: "/app/children", label: "Children", icon: Users, testid: "nav-children" },
  { to: "/app/court-bundle", label: "Court Bundle", icon: FileDown, testid: "nav-court-bundle" },
  // v77.99.140 (iter 124.exports-inbox) — "Exports" surfaces the async
  // job history for every generated document (Court Bundles + Expenses
  // PDFs today, Contact Visit + AI reports later). Sits under Court
  // Bundle because both are places you go to fetch a rendered PDF.
  { to: "/app/exports", label: "Exports", icon: Inbox, testid: "nav-exports" },
  // v77.99.36 — Insights sits right after Court Bundle because both
  // are "look-at-the-pattern" views rather than day-to-day data entry.
  { to: "/app/insights", label: "Insights", icon: TrendingUp, testid: "nav-insights" },
  // v77.49 — Third-party (mediator / solicitor / professional) access
  // sits directly under Court Bundle: both are documents you'd hand
  // to a legal/clinical professional, so they belong next to each
  // other in the user's mental model.
  { to: "/app/third-party-access", label: "Third-party access", icon: UserCog, testid: "nav-third-party-access" },
  { to: "/app/help", label: "Help", icon: HelpCircle, testid: "nav-help" },
  { to: "/app/settings", label: "Settings", icon: Settings, testid: "nav-settings" },
];

// Operator-only navigation — surfaces the admin tools and nothing else.
// Settings still appears so the operator can change password / 2FA / sign
// out, but everything user-facing is gone. Help is omitted intentionally —
// admins are the people writing the help content, not consuming it.
const OPERATOR_NAV = [
  { to: "/app/admin", label: "Admin", icon: ShieldCheck, testid: "nav-admin" },
  { to: "/app/settings", label: "Settings", icon: Settings, testid: "nav-settings" },
];

export default function AppShell() {
  const { user, logout, isMediator, canView, refresh } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [moreOpen, setMoreOpen] = useState(false);
  // v77.43 — Bug report dialog state, hosted at the shell so EVERY page
  // can open it without re-mounting the component tree.
  const [bugOpen, setBugOpen] = useState(false);
  // v77.99.20 — What's-new modal. The sidebar link shows an unread
  // dot when the user hasn't yet opened the modal since the latest
  // release date. v77.99.21: latest release info is now fetched live
  // from `/api/release-notes` (admin-editable). Fallback uses the
  // baked-in static array so first-paint never lags.
  const [whatsNewOpen, setWhatsNewOpen] = useState(false);
  const [latestReleaseDate, setLatestReleaseDate] = useState(FALLBACK_LATEST_DATE);
  const [latestReleaseVersion, setLatestReleaseVersion] = useState(FALLBACK_LATEST_VERSION);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/release-notes");
        if (cancelled) return;
        if (data?.latest_date) setLatestReleaseDate(data.latest_date);
        if (data?.latest_version) setLatestReleaseVersion(data.latest_version);
      } catch {
        // Keep fallbacks
      }
    })();
    return () => { cancelled = true; };
  }, []);
  // v77.99.150 (iter 122.onboarding-restore) — New sign-ups must
  // land in the three-step wizard before the app can be useful. The
  // redirect was lost in the Phase D1 Dashboard refactor. Fire the
  // status check once per session (not on every route change) and
  // only redirect if the user hasn't already opted out. Skip for
  // mediators / operators — the backend forces every flag off for
  // mediators, and operators shouldn't see parent-facing setup.
  useEffect(() => {
    if (!user) return undefined;
    if (user?.is_primary_admin) return undefined;
    if ((user?.role || "").toLowerCase() === "mediator") return undefined;
    // Don't fight the wizard if the user is already on it, or on a
    // route that has its own bootstrap (auth callback, etc.).
    if (location.pathname === "/app/onboarding") return undefined;
    let cancelled = false;
    api.get("/onboarding/status").then((r) => {
      if (cancelled) return;
      if (r.data?.show_full_onboarding) {
        navigate("/app/onboarding", { replace: true });
      }
    }).catch((err) => {
      // eslint-disable-next-line no-console
      console.warn("[appshell] onboarding status failed:", err?.response?.status || err?.message);
    });
    return () => { cancelled = true; };
    // Deliberately keyed on user.id: refire once per authenticated user,
    // not on every route change (would loop redirects while the wizard loads).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);
  const lastSeen = (() => {
    try { return localStorage.getItem("cpc.whatsNew.lastSeen") || ""; }
    catch { return ""; }
  })();
  const hasUnreadRelease = lastSeen < latestReleaseDate;
  const openWhatsNew = () => {
    setWhatsNewOpen(true);
    try { localStorage.setItem("cpc.whatsNew.lastSeen", latestReleaseDate); }
    catch { /* private mode / quota — non-fatal */ }
  };

  const isOperator = !!user?.is_primary_admin;
  const isAdmin = !!user?.is_admin;
  // Operator accounts get a stripped-down nav with admin-only entries.
  // Mediators (limited-permission third parties) only see areas they have
  // permission for. Everyone else sees the full parent-facing app.
  // Regular admins (ADMIN_EMAILS allow-list) see the parent app but with
  // the Help link hidden — admins curate the help content for parents,
  // they don't consume it themselves.
  //
  // v77.99.68 — For mediators we relabel the "/app/third-party-access"
  // entry to "Switch client" since they're not granting access — they
  // already are the third party and want a one-tap return to the
  // client list to switch families.
  const visibleNav = isOperator
    ? OPERATOR_NAV
    : NAV.filter((n) => {
        if (n.section) return true; // section headers always retained, pruned later
        if (n.to === "/app/help" && isAdmin) return false;
        return !n.permKey || canView(n.permKey);
      }).map((n) => {
        if (n.section) return n;
        if (isMediator && n.to === "/app/third-party-access") {
          return { ...n, to: "/app/third-party", label: "Switch client", icon: ArrowLeftRight, testid: "nav-switch-client" };
        }
        return n;
      }).filter((n, i, arr) => {
        // Drop section headers that have no following items before the next header.
        if (!n.section) return true;
        const next = arr.slice(i + 1).find((x) => !!x);
        if (!next) return false;
        return !next.section;
      });
  const MOBILE_FLAT = visibleNav.filter((n) => !n.section);
  // v77.99.99 — Mobile bottom-nav. Dashboard + More are fixed anchors;
  // the three middle slots come from the central module registry
  // (/lib/navModules.js) via `resolveMobileNavSlots`. This keeps the
  // pin-list in lock-step with the More-menu starring affordance below.
  const dashboardEntry = MOBILE_FLAT.find((n) => n.to === "/app/dashboard");
  const resolvedSlotModules = resolveMobileNavSlots(user);
  // Map slugs back to NAV items so we keep the sidebar's testid + perm
  // metadata (resolveMobileNavSlots returns registry entries that share
  // the same `to` path).
  const navByPath = Object.fromEntries(MOBILE_FLAT.map((n) => [n.to, n]));
  const resolvedSlots = resolvedSlotModules
    .map((m) => navByPath[m.to])
    .filter(Boolean);
  const MOBILE_PRIMARY = [dashboardEntry, ...resolvedSlots].filter(Boolean);
  const primaryPaths = new Set(MOBILE_PRIMARY.map((n) => n.to));
  // The "More" sheet must contain every pinnable module — including
  // those that don't have a sidebar entry (e.g. activity-log,
  // report-a-bug). Merge the registry into MOBILE_FLAT so the ⭐
  // toggle is available on every pinnable item.
  const flatPaths = new Set(MOBILE_FLAT.map((n) => n.to));
  const registryExtras = MOBILE_NAV_MODULES
    .filter((m) => moduleVisibleTo(m, user) && !flatPaths.has(m.to))
    .map((m) => ({
      to: m.to,
      label: m.label,
      icon: m.icon,
      testid: `nav-${m.slug}`,
      permKey: m.permKey,
    }));
  const MOBILE_SECONDARY = [
    ...MOBILE_FLAT.filter((n) => !primaryPaths.has(n.to)),
    ...registryExtras,
  ];
  // ⭐ favourites toggle in More — pins/unpins a module without leaving
  // the More menu. Stored on the user document via the same endpoint
  // that powers Settings → Customise Navigation.
  const pinnedSlugs = new Set(resolvedSlotModules.map((m) => m.slug));
  const slugForNavItem = (n) => (n?.to || "").split("/").pop();
  const isPinned = (n) => pinnedSlugs.has(slugForNavItem(n));
  const togglePin = async (n) => {
    const slug = slugForNavItem(n);
    if (!slug) return;
    const current = resolvedSlotModules.map((m) => m.slug);
    let nextSlots;
    if (current.includes(slug)) {
      nextSlots = current.filter((s) => s !== slug);
    } else if (current.length >= 3) {
      // First-three-favourites rule: drop the oldest pinned to make room.
      nextSlots = [...current.slice(1), slug];
    } else {
      nextSlots = [...current, slug];
    }
    try {
      await api.patch("/me/mobile-nav", { slots: nextSlots });
      await refresh?.();
    } catch (e) {
      // Silent — the More menu's toast feedback would be heavy here.
      console.warn("Could not update favourite", e?.response?.data || e);
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  // Pulse-highlight any element pointed to by ?spotlight=<testid> after a
  // Help-page deep-link. Mounted once at the shell so every in-app page
  // benefits without per-page wiring.
  useDeepLinkSpotlight();

  return (
    <div className="min-h-screen bg-[#F5F7F8] text-[#2E3A3F] flex">
      {/* Sidebar (desktop) */}
      <aside className="hidden lg:flex w-64 flex-col border-r border-[#D9E1E3] bg-white sticky top-0 h-screen">
        <div className="h-16 flex items-center px-5 border-b border-[#D9E1E3]">
          <Logo size={28} className="mr-2" />
          <span className="font-heading font-semibold">CoParent Console</span>
        </div>
        <nav className="flex-1 px-3 py-4 space-y-0.5">
          {visibleNav.map((item, idx) => {
            if (item.section) {
              return (
                <div
                  key={`section-${item.section}-${idx}`}
                  className={`px-3 text-[10px] uppercase tracking-[0.12em] font-medium text-[#6F828A]/70 ${idx === 0 ? "pt-2 pb-1" : "pt-4 pb-1"}`}
                  data-testid={`nav-section-${item.section.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  {item.section}
                </div>
              );
            }
            const { to, label, icon: Icon, testid } = item;
            return (
              <NavLink
                key={to} to={to} data-testid={testid}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2 rounded-sm text-sm transition-colors duration-200 ${
                    isActive ? "bg-[#E8F0F1] text-[#2E3A3F] font-medium" : "text-[#6F828A] hover:bg-[#EEF3F4] hover:text-[#2E3A3F]"
                  }`
                }
              >
                <Icon className="w-4 h-4" strokeWidth={1.5} /> {label}
              </NavLink>
            );
          })}
          {/* v77.99.20 — What's new modal trigger. Not a route — opens a
              modal. Adjacent to nav so the unread dot is visible
              everywhere in the app. Hidden for mediators since the
              releases reference parent-facing features. */}
          {!isMediator && (
            <button
              type="button"
              onClick={openWhatsNew}
              data-testid="nav-whats-new"
              className="flex items-center gap-3 px-3 py-2 rounded-sm text-sm w-full text-left text-[#6F828A] hover:bg-[#EEF3F4] hover:text-[#2E3A3F] transition-colors duration-200"
            >
              <Sparkles className="w-4 h-4" strokeWidth={1.5} />
              <span className="flex-1">What's new</span>
              {hasUnreadRelease && (
                <span
                  className="w-2 h-2 rounded-full bg-[#1F9FA4] inline-block"
                  data-testid="nav-whats-new-unread-dot"
                  title={`New in ${latestReleaseVersion}`}
                />
              )}
            </button>
          )}
        </nav>
        <div className="p-3 border-t border-[#D9E1E3]">
          <div className="px-3 py-2 text-sm text-[#6F828A] flex items-center gap-2">
            <span className="truncate flex-1" data-testid="sidebar-user-name">{user?.name}</span>
            <ThemeToggle />
          </div>
          <Button variant="ghost" size="sm" className="w-full justify-start text-[#6F828A] hover:bg-[#EEF3F4]" onClick={handleLogout} data-testid="logout-button">
            <LogOut className="w-4 h-4 mr-2" strokeWidth={1.5} /> Sign out
          </Button>
        </div>
      </aside>

      {/* v77.99.27 — `min-w-0` is the canonical fix for the
          "child forces flex item to grow past parent" CSS gotcha.
          Without it, any descendant with an intrinsic min-content
          width (e.g. the Calendar Week View's `min-w-[720px]`
          inner grid) would push the entire main column past the
          viewport on phones, causing the whole page to scroll
          horizontally. With `min-w-0` the flex item correctly
          clamps to its parent's content box and overflow-x-auto
          on the inner Week View container actually engages. */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Mobile header */}
        <header className="lg:hidden sticky top-0 z-20 bg-white border-b border-[#D9E1E3]">
          <div className="px-4 h-14 flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 min-w-0">
              <Logo size={28} />
              <span className="font-heading font-semibold truncate">CoParent Console</span>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              {isMediator && user?.active_client_label && (
                <NavLink
                  to="/app/third-party"
                  className="inline-flex items-center gap-1 text-[11px] font-medium text-[#116B6E] bg-[#D6F0EF] border border-[#A6D7D9] rounded-sm px-2 py-1 max-w-[140px]"
                  data-testid="mediator-viewing-pill-mobile"
                  title={`Viewing: ${user.active_client_label} · Tap to switch client`}
                  aria-label={`Viewing ${user.active_client_label}. Tap to switch client.`}
                >
                  <Eye className="w-3 h-3 flex-shrink-0" strokeWidth={1.8} />
                  <span className="truncate">{user.active_client_label}</span>
                </NavLink>
              )}
              <ChildSwitcher testIdSuffix="-mobile" />
              {/* Iter 122.trial-pivot — Mobile trial pill. Hidden for
                  paid / admin / complimentary / third-party / locked. */}
              {!isOperator && !isMediator && <TrialPill compact testid="trial-pill-mobile" />}
              <Button variant="ghost" size="sm" onClick={handleLogout} data-testid="mobile-logout-button">
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </header>

        <main className="flex-1 pb-24 lg:pb-8 min-w-0 max-w-full overflow-x-hidden">
          {/* Desktop top-right utility bar — child switcher lives here so
              the user can scope the entire app from any page. Hidden on
              mobile (which has its own header above with the switcher).
              v77.99.68 — Mediators also get a persistent "Viewing: {client}"
              pill so they always know which family's data they're looking
              at, and have a one-tap path back to the client list. */}
          <div className="hidden lg:flex sticky top-0 z-10 bg-[#F5F7F8] border-b border-transparent">
            <div className="ml-auto flex items-center gap-3 px-6 lg:px-8 py-3">
              {isMediator && user?.active_client_label && (
                <NavLink
                  to="/app/third-party"
                  className="inline-flex items-center gap-1.5 text-xs font-medium text-[#116B6E] bg-[#D6F0EF] border border-[#A6D7D9] rounded-sm px-2.5 py-1 hover:bg-[#C8E8E7] transition-colors"
                  data-testid="mediator-viewing-pill"
                  title="Click to switch client"
                >
                  <Eye className="w-3.5 h-3.5 flex-shrink-0" strokeWidth={1.8} />
                  <span className="text-[#6F828A] uppercase tracking-[0.08em] text-[10px] font-medium">Viewing</span>
                  <span className="text-[#0F5658] max-w-[200px] truncate">{user.active_client_label}</span>
                </NavLink>
              )}
              {/* Iter 122.trial-pivot — Desktop trial pill. Sits to
                  the left of ChildSwitcher so it's visible without
                  competing with primary actions. */}
              {!isOperator && !isMediator && <TrialPill testid="trial-pill-desktop" />}
              <ChildSwitcher testIdSuffix="-desktop" />
            </div>
          </div>
          {!isOperator && <SubscriptionBanner />}
          {isMediator && (
            <div className="bg-[#D6F0EF] border-b border-[#1F9FA4] text-[#116B6E] text-xs" data-testid="mediator-banner">
              <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2 flex items-center gap-2">
                <span className="uppercase tracking-[0.1em] font-medium">View-only access</span>
                <span className="text-[#116B6E]/80">You can read this household's records but cannot send, edit, upload or flag.</span>
              </div>
            </div>
          )}
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 min-w-0">
            <NoChildrenAccessBanner />
            {!isOperator && !isMediator ? (
              <div className="mb-4 lg:hidden" data-testid="install-prompt-slot">
                <InstallPrompt />
              </div>
            ) : null}
            <Outlet />
          </div>
        </main>

        {/* Bottom nav (mobile) — 4 primary + More
            • Subtle top shadow separates the bar from the content below it
            • Active tab uses brand teal with a thin top accent so the selected
              section reads at a glance without adding any new structure. */}
        <nav
          className="lg:hidden fixed bottom-0 inset-x-0 bg-white border-t border-[#D9E1E3] z-20 pb-[env(safe-area-inset-bottom)] shadow-[0_-2px_12px_rgba(46,58,63,0.08)]"
          data-testid="mobile-bottom-nav"
        >
          <div className="grid grid-cols-5">
            {MOBILE_PRIMARY.map(({ to, label, icon: Icon, testid }) => (
              <NavLink
                key={to} to={to} data-testid={`mobile-${testid}`}
                className={({ isActive }) =>
                  `relative flex flex-col items-center gap-1 py-2 text-[11px] transition-colors ${
                    isActive ? "text-[#116B6E] font-medium" : "text-[#6F828A] hover:text-[#2E3A3F]"
                  }`
                }
              >
                {({ isActive }) => (
                  <>
                    {isActive && (
                      <span aria-hidden="true" className="absolute top-0 inset-x-4 h-[2px] rounded-full bg-[#1F9FA4]" />
                    )}
                    <Icon className="w-5 h-5" strokeWidth={isActive ? 1.75 : 1.5} />
                    {label}
                  </>
                )}
              </NavLink>
            ))}
            <Sheet open={moreOpen} onOpenChange={setMoreOpen}>
              <SheetTrigger asChild>
                <button
                  type="button"
                  className={`relative flex flex-col items-center gap-1 py-2 text-[11px] transition-colors ${moreOpen ? "text-[#116B6E] font-medium" : "text-[#6F828A] hover:text-[#2E3A3F]"}`}
                  data-testid="mobile-nav-more"
                >
                  {moreOpen && (
                    <span aria-hidden="true" className="absolute top-0 inset-x-4 h-[2px] rounded-full bg-[#1F9FA4]" />
                  )}
                  <MoreHorizontal className="w-5 h-5" strokeWidth={moreOpen ? 1.75 : 1.5} />
                  More
                </button>
              </SheetTrigger>

              {/* Expanded "More" menu — feels like a distinct modal layer:
                   • Darker backdrop overlay is provided by SheetOverlay (bg-black/80)
                   • Stronger elevation: rounded-t-2xl, shadow-2xl, thicker border-t
                   • Larger padding and spacing for easy thumb navigation
                   • Clearly visible Close (X) button in the header row. */}
              <SheetContent
                side="bottom"
                className="bg-white rounded-t-2xl border-t-2 border-[#D9E1E3] shadow-2xl p-6 pt-5 pb-[calc(env(safe-area-inset-bottom)+1.5rem)] max-h-[85vh] overflow-y-auto [&>button.absolute]:hidden"
                data-testid="mobile-more-sheet"
              >
                {/* Grabber handle for visual affordance that this is a sheet */}
                <div className="mx-auto w-10 h-1 rounded-full bg-[#D9E1E3] mb-4" aria-hidden="true" />
                <SheetHeader className="flex-row items-center justify-between space-y-0 pb-1">
                  <SheetTitle className="font-heading text-lg text-[#2E3A3F]">Menu</SheetTitle>
                  <SheetClose asChild>
                    <button
                      type="button"
                      aria-label="Close menu"
                      className="inline-flex items-center justify-center w-10 h-10 -mr-2 rounded-full text-[#2E3A3F] hover:bg-[#EEF3F4] active:bg-[#DDE7E8] transition-colors"
                      data-testid="mobile-more-close"
                    >
                      <X className="w-5 h-5" strokeWidth={1.75} />
                    </button>
                  </SheetClose>
                </SheetHeader>
                {/* v77.99.108e (iter 122.menu-coach) — discoverability
                    helper. Subtle one-line cue beneath the Menu title.
                    Flips to a pinned-count once the user has pinned
                    anything. Hidden when 3 of 3 are pinned. */}
                {pinnedSlugs.size < 3 ? (
                  <p
                    className="text-xs text-[#6F828A] leading-snug -mt-1 mb-1"
                    data-testid="mobile-more-coach"
                  >
                    {pinnedSlugs.size === 0
                      ? "Tap the \u2605 to pin up to 3 shortcuts to your bottom navigation."
                      : `You've pinned ${pinnedSlugs.size} of 3 shortcuts. Tap a \u2605 to add another.`}
                  </p>
                ) : null}
                <div className="mt-4 grid grid-cols-2 gap-3 items-stretch">
                  {MOBILE_SECONDARY.map((item) => {
                    const { to, label, icon: Icon, testid } = item;
                    const pinned = isPinned(item);
                    // Iter 122.menu-coach testid-fix — strip the legacy
                    // `nav-` prefix so external automation can target
                    // `mobile-more-card-<slug>` / `mobile-more-fav-<slug>`
                    // exactly as documented in the design system spec.
                    const slug = slugForNavItem(item) || (testid || "").replace(/^nav-/, "");
                    return (
                      <div key={to} className="relative h-full" data-testid={`mobile-more-card-${slug}`}>
                        <NavLink
                          to={to}
                          onClick={() => setMoreOpen(false)}
                          data-testid={`mobile-more-${testid}`}
                          className={({ isActive }) =>
                            // Iter 122.menu-grid-responsive — Vertical
                            // layout (icon stacked above label) so even
                            // long labels like "Third-party access" and
                            // "Upcoming Expenses" get the FULL tile
                            // width for wrap. We keep px-3 / py-3 for
                            // breathable padding without robbing label
                            // width, and pr-9 reserves a precise 36px
                            // gutter for the ⭐ in the top-right
                            // corner. `min-h-[92px]` keeps every tile
                            // the same height across a row even when a
                            // sibling has a 2-line label.
                            `flex flex-col items-start gap-2 px-3 pt-3 pr-9 pb-3 border rounded-md text-sm transition-colors h-full min-h-[92px] ${
                              isActive
                                ? "bg-[#E0F2F2] border-[#1F9FA4] text-[#116B6E] font-medium"
                                : "bg-white border-[#D9E1E3] text-[#2E3A3F] hover:bg-[#EEF3F4] hover:border-[#BFCCD0]"
                            }`
                          }
                        >
                          <Icon className="w-5 h-5 flex-shrink-0" strokeWidth={1.5} />
                          {/* `break-words` + `hyphens-auto` lets long
                              compound labels wrap rather than getting
                              clipped. `leading-snug` keeps two-line
                              wraps tight without ascender clipping. */}
                          <span className="leading-snug break-words hyphens-auto w-full">{label}</span>
                        </NavLink>
                        {/* ⭐ favourite. Tapping pins/unpins this
                            module into the mobile bottom-nav. If 3
                            are already pinned, the oldest is
                            replaced (first-three-favourites rule).
                            Absolute top-right with its own hit
                            target so it never sits on top of the
                            label. */}
                        <button
                          type="button"
                          onClick={(e) => { e.preventDefault(); e.stopPropagation(); togglePin(item); }}
                          aria-label={pinned ? `Unpin ${label} from bottom nav` : `Pin ${label} to bottom nav`}
                          aria-pressed={pinned ? "true" : "false"}
                          title={pinned ? "Pinned to bottom nav" : "Pin to bottom nav"}
                          data-testid={`mobile-more-fav-${slug}`}
                          className={`absolute top-1.5 right-1.5 inline-flex items-center justify-center w-7 h-7 rounded-full transition-colors ${
                            pinned
                              ? "text-amber-500 bg-amber-50/60 hover:bg-amber-100"
                              : "text-[#C2CCCF] hover:text-amber-500 hover:bg-[#EEF3F4]"
                          }`}
                        >
                          <Star className={`w-4 h-4 ${pinned ? "fill-amber-500" : ""}`} strokeWidth={1.6} />
                        </button>
                      </div>
                    );
                  })}
                  {/* v77.43 — Report a bug stays as a static button
                      because it opens an in-app modal rather than
                      navigating to a route. Activity Log is now driven
                      by the central registry (so it gets the ⭐ toggle
                      automatically and avoids the dupe entry the
                      v77.99.99 refactor briefly introduced). */}
                  {!isOperator && (
                    <button
                      type="button"
                      onClick={() => { setMoreOpen(false); setBugOpen(true); }}
                      data-testid="mobile-more-nav-report-bug"
                      className="flex flex-col items-start gap-2 px-3 pt-3 pr-9 pb-3 border rounded-md text-sm transition-colors bg-white border-[#D9E1E3] text-[#2E3A3F] hover:bg-[#EEF3F4] hover:border-[#BFCCD0] text-left h-full min-h-[92px]"
                    >
                      <Bug className="w-5 h-5 flex-shrink-0" strokeWidth={1.5} />
                      <span className="leading-snug break-words hyphens-auto w-full">Report a bug</span>
                    </button>
                  )}
                </div>
                {/* v77.20.1 — Quick appearance toggle right inside the
                    mobile More sheet so users can flip light/dark/system
                    without diving into Settings. */}
                <div className="mt-3">
                  <ThemeToggle variant="row" />
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </nav>
      </div>

      {/* v77.43 — Bug report dialog. Hosted at the shell so any page
          (including Help) can open it via the same instance. */}
      <BugReportDialog open={bugOpen} onOpenChange={setBugOpen} />
      {/* v77.99.20 — What's-new modal. Mounted at the shell. */}
      <WhatsNewModal open={whatsNewOpen} onOpenChange={setWhatsNewOpen} />
    </div>
  );
}
